import type {
  LayoutAlign,
  LayoutBackground,
  LayoutDirection,
  LayoutGap,
  LayoutJustify,
  LayoutMaxWidth,
} from "./types";
import { ALIGN_CLASS, MAX_WIDTH_CLASS, cx } from "./internal";
import { resolveGapLayout } from "./gap";
import { CMS_FLOW_CLASS } from "@/lib/cms-puck/block-style/defaults";

const PADDING_X_CLASS: Record<LayoutGap, string> = {
  none: "px-0",
  sm: "px-2",
  md: "px-4",
  lg: "px-8",
  xl: "px-12",
};

const PADDING_Y_CLASS: Record<LayoutGap, string> = {
  none: "py-0",
  sm: "py-2",
  md: "py-4",
  lg: "py-8",
  xl: "py-12",
};

const JUSTIFY_CLASS: Record<LayoutJustify, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

const BACKGROUND_CLASS: Record<LayoutBackground, string> = {
  none: "",
  muted: "bg-slate-100",
  dark: "bg-slate-900 text-white",
};

/** Fond structurel du conteneur (pleine largeur du wrapper, hors maxWidth). */
export function containerBackgroundClass(
  background: LayoutBackground,
): string {
  return BACKGROUND_CLASS[background];
}

/** Classe Tailwind du bloc Container (flex). */
export function containerClass(props: {
  direction: LayoutDirection;
  align: LayoutAlign;
  justify: LayoutJustify;
  gap: LayoutGap;
  gapCustomPx?: number | null;
  /** @deprecated Utiliser paddingX / paddingY. Conservé pour rétro-compat. */
  padding?: LayoutGap;
  paddingX?: LayoutGap;
  paddingY?: LayoutGap;
  maxWidth: LayoutMaxWidth;
  background: LayoutBackground;
  /** Retour à la ligne des enfants (row) : `wrap` (défaut) · `nowrap` (1 ligne, ex. en-tête). */
  wrap?: "wrap" | "nowrap";
}): string {
  const px = props.paddingX ?? props.padding ?? "none";
  const py = props.paddingY ?? props.padding ?? "none";
  const gap = resolveGapLayout({
    gap: props.gap,
    gapCustomPx: props.gapCustomPx,
  });
  const rowWrap = props.wrap === "nowrap" ? "flex-row flex-nowrap" : "flex-row flex-wrap";
  return cx(
    "flex w-full",
    CMS_FLOW_CLASS,
    props.direction === "row" ? rowWrap : "flex-col",
    ALIGN_CLASS[props.align],
    JUSTIFY_CLASS[props.justify],
    gap.className,
    PADDING_X_CLASS[px],
    PADDING_Y_CLASS[py],
    MAX_WIDTH_CLASS[props.maxWidth] ?? "",
    BACKGROUND_CLASS[props.background],
  );
}
